import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import GetApp from "./GetApp";
import { SPRING } from "../lib/motion";
import "./sticky-cta.css";

/**
 * A call to action that follows the reader down the page.
 *
 * Visible only between the two real ones: it arrives after the hero's buttons
 * have gone, and leaves before the closing section's arrive. Two identical
 * pink buttons on screen at the same time is how a reader learns to ignore
 * both of them.
 *
 * The end of its range is found from the element the page marks with
 * `data-cta-end` rather than from a pixel offset, so rewriting a section above
 * it cannot silently break the handoff.
 *
 * Bottom-right on a wide screen, where no text runs; a bar along the bottom
 * edge on a phone. It says "free" and not how long: the trial length is on the
 * pricing cards, where the reader is choosing.
 */
export default function StickyCta() {
  const reduce = useReducedMotion();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    /*
     * Three observers instead of a scroll handler: past the hero, not yet at
     * the close, and not over a section with its own controls along the
     * bottom edge (the pinned day timeline). The browser tells us when each
     * changes; nothing runs while the reader scrolls.
     */
    const state = { past: false, before: true, yielded: false };
    const update = () => setVisible(state.past && state.before && !state.yielded);
    const observers = [];
    const watch = (els, options, fn) => {
      if (!els.length) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach(fn);
        update();
      }, options);
      els.forEach((el) => io.observe(el));
      observers.push(io);
    };

    const hero = document.querySelector(".hero");
    if (hero) watch([hero], { rootMargin: "0px 0px -10% 0px" }, (e) => (state.past = !e.isIntersecting && e.boundingClientRect.top < 0));
    else state.past = true;
    watch([...document.querySelectorAll("[data-cta-end]")], { rootMargin: "0px 0px -15% 0px" }, (e) => (state.before = !e.isIntersecting && e.boundingClientRect.top > 0));
    const yielding = new Map();
    watch([...document.querySelectorAll("[data-cta-yield]")], { rootMargin: "-50% 0px -50% 0px" }, (e) => {
      yielding.set(e.target, e.isIntersecting);
      state.yielded = [...yielding.values()].some(Boolean);
    });
    update();

    return () => observers.forEach((io) => io.disconnect());
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="sticky-cta"
          initial={
            reduce
              ? { opacity: 0 }
              : { opacity: 0, y: 24, scale: 0.96 }
          }
          animate={
            reduce
              ? { opacity: 1 }
              : { opacity: 1, y: 0, scale: 1 }
          }
          /*
           * It leaves faster than it arrives, and downward — the direction it
           * came from. An exit that lingers is an exit the reader watches.
           */
          exit={
            reduce
              ? { opacity: 0 }
              : { opacity: 0, y: 16, scale: 0.98 }
          }
          transition={
            reduce
              ? { duration: 0.15 }
              : { ...SPRING.sheet, exit: { duration: 0.16 } }
          }
        >
          <span className="sticky-cta__text">
            Try it free
            <span className="sticky-cta__sub">No card needed.</span>
          </span>

          <GetApp size="sm" short link={false} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
